// UTILS
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useCustomerNavigate } from 'hooks/useCustomerNavigate';
import { useNumericIdRouteParam } from 'hooks/useNumericIdRouteParam';
import { useAppDispatch } from 'slices/store';
import type { TRolePermission } from '@timeedit/types/lib/types';
import { Button, DefaultContentLayout } from '@timeedit/ui-components';
import { theme } from '@timeedit/ui-components/antd';
import ListViewHOC from '../../../components/ListView';
import TreeListView from '../../../components/ListView/TreeListView';
import LoadingWrapper from '../../../components/LoadingWrapper';
// REDUX
import {
  createRolePermission,
  deleteRolePermission,
  fetchRolePermissionGroups,
  fetchRolePermissions,
  rolePermissionSelector,
  rolePermissionsLoading,
  rolePermissionsSelector,
  saveRolePermission,
  selectRolePermissionsAsTreeData,
} from '../../../slices/rolePermission.slice';
// COMPONENTS
import RolePermissionDetailPage from './detail';

const RolePermissionPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useCustomerNavigate();
  const rolePermissionId = useNumericIdRouteParam();
  const { token } = theme.useToken();

  /**
   * SELECTORS
   */
  const rolePermissions = useSelector(rolePermissionsSelector);
  const rolePermissionTree = useSelector(selectRolePermissionsAsTreeData);
  const originalRolePermission = useSelector(
    rolePermissionSelector(rolePermissionId)
  );
  const isLoading = useSelector(rolePermissionsLoading);

  /**
   * STATE
   */
  const [rolePermission, setRolePermission] = useState<
    TRolePermission | undefined
  >(originalRolePermission);

  /**
   * EFFECTS
   */
  useEffect(() => {
    // Jumping to newly saved new config
    if (
      rolePermissionId === 0 &&
      rolePermissions.findIndex((el: TRolePermission) => el.id === 0) === -1
    )
      navigate(
        `/user-management/role-permissions/${
          rolePermissions[rolePermissions.length - 1].id
        }`,
        { replace: true }
      );
    // Default case
    if (
      rolePermissionId == null &&
      rolePermissions &&
      rolePermissions[0] &&
      rolePermissions[0].id
    )
      navigate(`/user-management/role-permissions/${rolePermissions[0].id}`, {
        replace: true,
      });
  }, [rolePermissionId, rolePermissions, navigate]);

  useEffect(() => {
    if (rolePermissionId !== rolePermission?.id)
      setRolePermission(originalRolePermission);
  }, [rolePermissionId, rolePermissions]);

  useEffect(() => {
    dispatch(fetchRolePermissionGroups());
    dispatch(fetchRolePermissions());
  }, []);

  /**
   * EVENT HANDLERS
   */
  const onSelectRolePermission = (rolePermissionId: string | number) => {
    navigate(`/user-management/role-permissions/${rolePermissionId}`);
  };

  // Adds new authentication config
  const onAddRolePermission = () => dispatch(createRolePermission());

  const onSelectRolePermissionGroup = (newRolePermissionGroupId: string) => {
    if (!rolePermission) return;
    if (newRolePermissionGroupId !== rolePermission.rolePermissionGroup)
      setRolePermission({
        ...rolePermission,
        rolePermissionGroup: newRolePermissionGroupId,
        groupActions: [],
      });
  };

  // Handle changes
  const onChange = (prop: string, value: any) => {
    if (!rolePermission) return;
    setRolePermission({ ...rolePermission, [prop]: value });
  };

  // Deletes chosen authentication config
  const onDeleteRolePermission = (
    rolePermissionIdToDelete?: string | number
  ) => {
    dispatch(deleteRolePermission(rolePermissionIdToDelete as number));
    if (rolePermissionId === rolePermissionIdToDelete)
      navigate('/user-management/role-permissions');
  };

  // Save changes function
  const onSaveRolePermission = async (rolePermission: TRolePermission) => {
    dispatch(saveRolePermission(rolePermission));
  };

  return (
    <DefaultContentLayout
      title="Role Permissions"
      subtitle="Role permissions for organization"
      actions={
        <>
          <Button
            onClick={() =>
              originalRolePermission &&
              setRolePermission(originalRolePermission)
            }
          >
            {'Discard'}
          </Button>
          <Button
            type="primary"
            onClick={() =>
              rolePermission && dispatch(saveRolePermission(rolePermission))
            }
          >
            {'Save'}
          </Button>
        </>
      }
    >
      <div
        style={{ display: 'flex', height: '100%', marginTop: token.marginXS }}
      >
        <ListViewHOC
          sectionHeading={'Role permissions'}
          number={(rolePermissions || []).length}
          onSelect={onAddRolePermission}
        >
          <TreeListView
            data={rolePermissionTree}
            onSelect={onSelectRolePermission}
            draggable={false}
            isLoading={isLoading}
            directoryTree
            fieldNames={{
              title: 'title',
              key: 'key',
              children: 'children',
            }}
          />
        </ListViewHOC>
        <div className="inner-content--wrapper allow-scroll">
          <LoadingWrapper isLoading={isLoading}>
            {rolePermissionId !== undefined && rolePermission && (
              <RolePermissionDetailPage
                onChange={onChange}
                onDelete={onDeleteRolePermission}
                onSelectRolePermissionGroup={onSelectRolePermissionGroup}
                rolePermission={rolePermission}
              />
            )}
          </LoadingWrapper>
        </div>
      </div>
    </DefaultContentLayout>
  );
};

export default RolePermissionPage;
